import { createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import _ from 'lodash';
import moment from 'moment-timezone';

import RefData from 'ref-data';

import { sharingNetworks, sharingNetworkFields } from '../../common/enums';

import ExperienceActions from './experience-actions';
import { reservedExperienceFields } from './experience-enums';

const initialState = Immutable({
  activeExperience: {
    //on the initial render, React elements will try to select the experience
    //getting null on this keys often results in UI crashes, so we need
    //to default these to something until the experience-model --> create method has time
    //to hydrate the experience (generally on the immediate second render)
    status:     {},
    typeRef:    {},
    subtypeRef: {},
  },
  isFetching:                            true,
  byId:                                  {},
  isConfirmDeleteExperienceModalVisible: false,
  isResetExperienceDataModalVisible:     false,
  isConfirmExpireExperienceModalVisible: false,
  isConfirmUpdateMetadataModalVisible:   false,
  dirtyFields:                           {},
  metrics:                               {},
  quotaStatus:                           {
    isLoading:      true,
    hasFetched:     false,
    statusMessages: [],
  },
});

// Form fields to be ignored when tracking if an experience is dirty.
// Because tracking the next fields as dirty would leave navigation blocked after flows like the metadata modal.
const IGNORED_DIRTY_FIELDS = [
  'updateExternalRefFromExperienceMetadata',
  'headerConfig',
  'footerConfig',
  ...reservedExperienceFields.ALL,
];

/**
 * Syncs dirty fields by comparing the current value with the saved value.
 * If values differ, marks the field as dirty. If they match, removes the field from dirtyFields.
 * @param {Object} state - The state with the updated field value
 * @param {string} key - The field key to track
 * @returns {Object} Updated state with synced dirty fields
 */
const _syncDirtyFields = (state, key) => {
  // Skip dirty tracking for reserved fields
  if (_.includes(IGNORED_DIRTY_FIELDS, key)) {
    return state;
  }

  // Track dirty field
  const originalExperience = state.byId[state.activeExperience.id];
  const oldValue = originalExperience[key];
  const newValue = state.getIn(['activeExperience', key]);
  if (!_.isEqual(oldValue, newValue)) {
    return state.setIn(['dirtyFields', key], true);
  }
  return state.set('dirtyFields', state.dirtyFields.without(key));
};

const reducer = createReducer((on) => {
  on([ExperienceActions.fetchExperienceSuccess, ExperienceActions.setActiveExperience], (state, { experience }) =>
    state.set('activeExperience', experience).set('dirtyFields', {})
  );

  on(ExperienceActions.fetchExperienceMetricsSuccess, (state, { summary }) => state.set('metrics', summary));

  on(ExperienceActions.fetchExperiences, (state) => state.set('isFetching', true));
  on(ExperienceActions.fetchExperiencesFailure, (state) => state.set('isFetching', false));
  on(ExperienceActions.fetchExperiencesSuccess, (state, { experiences }) => {
    const _existingExperiences = _.clone(state.byId);
    const byId = _.reduce(
      experiences,
      (acc, experience) => {
        acc[experience.id] = experience;
        return acc;
      },
      _existingExperiences
    );
    return state.merge({ isFetching: false, byId });
  });

  on(ExperienceActions.clearExperiences, (state) => state.set('byId', initialState.byId));

  on(ExperienceActions.updateExperienceSuccess, (state, { experience }) => {
    const newState = state.merge({ activeExperience: experience }, { deep: true });
    const { id } = newState.activeExperience;
    // also saving to byId to keep the state in sync with the backend (if need to compare state with backend)
    return newState.setIn(['byId', id], newState.activeExperience);
  });
  on(ExperienceActions.changeDisplayType, (state, { displayType }) => {
    const newState = state.setIn(['activeExperience', 'displayType'], RefData.Model.hydrate(displayType));
    return _syncDirtyFields(newState, 'displayType');
  });
  on(ExperienceActions.updateNavConfig, (state, { key, config }) => {
    const newState = state.setIn(['activeExperience', 'navConfig', key], config);
    return _syncDirtyFields(newState, 'navConfig');
  });

  on(ExperienceActions.updateTheme, (state, { experience }) => {
    return state.set('activeExperience', experience);
  });

  on(ExperienceActions.updateThemeFailure, (state, { themeId }) =>
    state.setIn(['activeExperience', 'themeId'], themeId)
  );

  on(ExperienceActions.changeExperience, (state, { key, value }) => {
    let newState = state.setIn(['activeExperience', key], value);

    if (key === 'timezone') {
      //when timezone is changed, we need to reset entry from/to limits(to support mapping between 00:00 <-> 00:30)
      const timezonePath = ['activeExperience', 'timezone'],
        oldTimezone = state.getIn(timezonePath),
        newTimezone = newState.getIn(timezonePath);
      if (oldTimezone !== newTimezone) {
        const entryFromPath = ['activeExperience', 'entryTimeFrom'],
          entryToPath = ['activeExperience', 'entryTimeTo'],
          oldEntryFromValue = state.getIn(entryFromPath),
          oldEntryToValue = state.getIn(entryToPath),
          newTimezoneOffsetAdjustment = Math.abs(moment.tz(newTimezone).utcOffset() % 60) * 60, //in seconds
          newEntryFromValue = _.isNumber(oldEntryFromValue)
            ? Math.floor(oldEntryFromValue / 3600) * 3600 + newTimezoneOffsetAdjustment
            : null,
          newEntryToValue = _.isNumber(oldEntryToValue)
            ? Math.floor(oldEntryToValue / 3600) * 3600 + newTimezoneOffsetAdjustment
            : null;
        newState = newState.setIn(entryFromPath, newEntryFromValue).setIn(entryToPath, newEntryToValue);
      }
    }

    return _syncDirtyFields(newState, key);
  });

  on(ExperienceActions.updateExperienceStatusSuccess, (state, { experience }) =>
    state.set('activeExperience', experience)
  );

  on(ExperienceActions.updateExperienceBackgroundSuccess, (state, { backgroundAssetRef, lastUpdated, modifiedBy }) => {
    const activeExperience = state.activeExperience.merge({ backgroundAssetRef, lastUpdated, modifiedBy });
    return state.merge({ activeExperience });
  });

  on(ExperienceActions.updateExperienceHeaderSuccess, (state, { headerConfig, lastUpdated, modifiedBy }) => {
    const activeExperience = state.activeExperience.merge({ headerConfig, lastUpdated, modifiedBy });
    return state.merge({ activeExperience });
  });

  on(ExperienceActions.updatePolicyConfigSuccess, (state, { policyConfig, policyConfigJSON }) => {
    const activeExperience = state.activeExperience.merge({ policyConfig, policyConfigJSON });
    return state.merge({ activeExperience });
  });

  on(ExperienceActions.showConfirmDeleteExperienceModal, (state) =>
    state.set('isConfirmDeleteExperienceModalVisible', true)
  );

  on(ExperienceActions.showConfirmExpireExperienceModal, (state) =>
    state.set('isConfirmExpireExperienceModalVisible', true)
  );

  on(ExperienceActions.showResetExperienceDataModal, (state) => state.set('isResetExperienceDataModalVisible', true));

  on(ExperienceActions.resetExperienceDataSuccess, (state, { experience }) =>
    state.merge({ activeExperience: experience }, { deep: true }).set('isResetExperienceDataModalVisible', false)
  );

  on([ExperienceActions.hideResetExperienceDataModal, ExperienceActions.resetExperienceDataFailure], (state) =>
    state.set('isResetExperienceDataModalVisible', false)
  );

  on(ExperienceActions.hideConfirmDeleteExperienceModal, (state) =>
    state.set('isConfirmDeleteExperienceModalVisible', false)
  );

  on(ExperienceActions.hideConfirmExpireExperienceModal, (state) =>
    state.set('isConfirmExpireExperienceModalVisible', false)
  );

  on(ExperienceActions.hideConfirmUpdateMetadataModal, (state) =>
    state.set('isConfirmUpdateExperienceModalVisible', false)
  );

  on(ExperienceActions.showConfirmUpdateMetadataModal, (state) =>
    state.set('isConfirmUpdateExperienceModalVisible', true)
  );

  on(ExperienceActions.resetDirtyFields, (state) => state.set('dirtyFields', {}));

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Sharing config.
  on(ExperienceActions.enableShare, (state, { network }) => {
    return state.setIn(['activeExperience', 'sharingConfig', network, 'enabled'], true);
  });

  on(ExperienceActions.disableShare, (state, { network }) => {
    return state.setIn(['activeExperience', 'sharingConfig', network, 'enabled'], false);
  });

  on(ExperienceActions.toggleShare, (state, { network }) => {
    // this will make sure that if the current sharing data for the given network is empty, it will copy the default sharing data
    // when the network is being enabled and leave it alone if it's already populated.
    // it will also only copy down the fields that are relevant for that network.

    // if toggling ON, defaults are filled in and chosen network IS NOT already customised, copy defaults to chosen network
    // if toggling ON, defaults are filled in and chosen network IS already customised, leave it as-is.
    // if toggling OFF, leave chosen network as-is

    const _fields = sharingNetworkFields[network];
    const _networkPath = state.activeExperience.sharingConfig[network];
    const _isCurrentlyEnabled = _networkPath.enabled;
    const _currentSharingData = _networkPath.data;
    const _isSharingDataEmpty = _.isEmpty(_.pickBy(_currentSharingData, (value) => !_.isEmpty(value))); //checking, the values are meaningful, not just empty keys filling in the object
    const _defaultSharingData = state.activeExperience.sharingConfig[sharingNetworks.DEFAULT].data;
    let _updatedSharingData;

    // This code block implements the following logic:
    // if we have enabled the sharing for network first time we just grab default values for it
    // if we than change it and disable it and enable again it will have changed values
    // but if you have not changed it at first enable it will grab new default values
    if (_isCurrentlyEnabled) {
      const hasChanges = _.some(_fields, (field) => _currentSharingData[field] !== _defaultSharingData[field]);

      if (hasChanges) {
        _updatedSharingData = _.pick(_currentSharingData, _fields);
      } else {
        _updatedSharingData = {};
        for (const key in _currentSharingData) {
          _updatedSharingData[key] = '';
        }
      }
    } else {
      _updatedSharingData = _.pick(_isSharingDataEmpty ? _defaultSharingData : _currentSharingData, _fields);
    }

    //check if any network in sharingConfig is enabled and if yes set sharing to true
    const sharing = _.some(state.activeExperience.sharingConfig, (config, key) => {
      const enabledSharing = config.enabled;
      return (
        ((key === network && !enabledSharing) || (key !== network && enabledSharing)) &&
        key !== 'campaign:share:default'
      );
    });

    return state
      .setIn(['activeExperience', 'sharingConfig', network, 'enabled'], !_networkPath.enabled)
      .setIn(['activeExperience', 'sharingConfig', network, 'data'], _updatedSharingData)
      .setIn(['activeExperience', 'sharing'], sharing);
  });

  on(ExperienceActions.updateSharingDataAttribute, (state, { id, value, network }) => {
    return state.setIn(['activeExperience', 'sharingConfig', network, 'data', id], value);
  });

  on(ExperienceActions.updateSharingData, (state, { sharingData, network }) => {
    return state.setIn(['activeExperience', 'sharingConfig', network, 'data'], sharingData);
  });

  on(ExperienceActions.updatePresentationConfig, (state, { field, value }) => {
    return state.setIn(['activeExperience', 'presentationConfig', field], value);
  });

  on(ExperienceActions.setQuotaStatus, (state, { quotaStatusMessages }) =>
    state.set('quotaStatus', {
      isLoading:      false,
      hasFetched:     true,
      statusMessages: quotaStatusMessages,
    })
  );
}, initialState);

export default reducer;
